import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import usePark from "common/hooks/usePark";
import { Animal } from "types/Animal";
import SectorSelect from "components/select/SectorSelect/SectorSelect";
import AnimalSelectSection from "components/select/AnimalSelect/AnimalSelectSection";
import { Tag } from "types/Tag";
import TagSelect from "components/select/TagSelect/TagSelect";
import { Enclosure } from "types/Enclosure";
import ObservationGroupSelect from "components/select/ObservationGroupSelect/ObservationGroupSelect";
import { ObservationGroup } from "types/ObservationGroup";
import useAuth from "common/hooks/useAuth";
import SpecySelect from "components/select/SpecySelect/SpecySelect";
import AnimalGenderSelect from "components/select/AnimalSelect/AnimalGenderSelect";
import AnimalSelect from "components/select/AnimalSelect/AnimalSelect";

type Props = {
  enclosure: Enclosure;
  onSubmit: (value?: any) => any;
};

function EnclosureForm({ onSubmit, enclosure }: Props) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const canManageEnclosure = enclosure?.id
    ? (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.enclosure?.update)) ??
      false
    : (currentUser?.isAdmin ||
        currentUser?.isSuperadmin ||
        ((currentUser?.isManager || currentUser?.isObserver) &&
          currentUser?.administrationPermissions?.enclosure?.create)) ??
      false;

  const initialValues = useMemo(
    () =>
      enclosure?.id
        ? {
            ...enclosure,
            park: enclosure.park?.id ?? currentPark?.id,
            animals: enclosure.animals?.map((a: Animal) => a.id),
            sector: enclosure.sector?.id,
            tags: enclosure?.tags?.map((tag: Tag) => tag.id),
            observationGroups: enclosure?.observationGroups?.map(
              (oG: ObservationGroup) => oG.id
            ),
          }
        : {
            name: "",
            description: "",
            park: currentPark?.id,
          },
    [enclosure, currentPark]
  );

  const additionalFilters = [
    {
      key: "species",
      name: t("animals.species"),
      component: <SpecySelect />,
    },
    {
      key: "tags",
      name: t("forms.tags"),
      component: <TagSelect />,
    },
    {
      key: "gender",
      name: t("animals.gender"),
      component: <AnimalGenderSelect />,
    },
  ];

  const groupAdditionalFilters = [
    {
      key: "species",
      name: t("animals.species"),
      component: <SpecySelect />,
    },
    {
      key: "tags",
      name: t("forms.tags"),
      component: <TagSelect />,
    },
    {
      key: "animals",
      name: t("forms.animals"),
      component: <AnimalSelect />,
    },
  ];

  const onSubmitWrapper = async (values: any) => {
    const res = await onSubmit(values);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ form, handleSubmit, values }) => (
        <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
          {/* Header */}
          <EditHeader
            title={t(
              enclosure?.id ? "editItem.enclosure" : "newItem.enclosure"
            )}
            subtitle={values.name}
            icon="TbFence"
            errorMessage={errorMessage}
            HeaderButtonsEdit={
              <HeaderButtonsEdit
                inHeader
                onSubmit={canManageEnclosure ? handleSubmit : undefined}
                onCancel={() => navigate("/enclosures")}
              />
            }
          />
          <form onSubmit={handleSubmit} noValidate>
            <FormScrollError />

            <div className="responsive-flex gap-2 mt-2">
              <div className="md:w-1/3 mb-2 md:mb-0">
                <div className="flex flex-col gap-2 w-full">
                  <div className="white-box">
                    <div className="flex flex-col gap-2">
                      <FormControl
                        name="name"
                        title={t("forms.name")}
                        required
                        containerStyle=""
                        editable={canManageEnclosure}
                      />
                      <FormControl
                        name="description"
                        title={t("forms.description")}
                        type="textarea"
                        className="w-full"
                        containerStyle="w-full"
                        rows={3}
                        editable={canManageEnclosure}
                      />
                      {!values.appliedToTheWholeOrganization && (
                        <FormControl
                          type="custom"
                          title={t("forms.sector")}
                          name="sector"
                          editable={canManageEnclosure}
                        >
                          <SectorSelect />
                        </FormControl>
                      )}
                      <FormControl
                        name="tags"
                        title={t("forms.tags")}
                        type="custom"
                        editable={canManageEnclosure}
                      >
                        <TagSelect multiple />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="md:w-2/3">
                <div className="flex flex-col gap-2 w-full">
                  <div className="white-box flex flex-col gap-2 justify-between items-center ">
                    <div className="flex-1 w-full gap-2 flex flex-col">
                      <FormControl
                        title={t("forms.observationGroups")}
                        type="custom"
                        editable={canManageEnclosure}
                        isSelect
                        name="observationGroups"
                      >
                        <ObservationGroupSelect
                          multiple
                          additionalFilters={groupAdditionalFilters}
                        />
                      </FormControl>
                      <FormControl
                        title={t("forms.animals")}
                        type="custom"
                        editable={canManageEnclosure}
                        isSelect
                        name="animals"
                      >
                        <AnimalSelectSection
                          form={form}
                          values={values}
                          additionalFilters={additionalFilters}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <HeaderButtonsEdit
              onSubmit={handleSubmit}
              onCancel={() => navigate("/enclosures")}
            />
          </form>
        </div>
      )}
    />
  );
}

export default EnclosureForm;
